// src/overscroll/OverscrollProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

const OverscrollCtx = createContext({
  enabled: true,
  setEnabled: () => {},
});

export function OverscrollProvider({ children, rootRef }) {
  const [enabled, setEnabled] = useState(true);
  const touchStartYRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // целевой элемент: приоритет у rootRef, иначе document.scrollingElement
    const target =
      (rootRef && rootRef.current) ||
      document.scrollingElement ||
      document.documentElement;

    if (!target) return;

    const onTouchStart = (e) => {
      if (!e.touches || !e.touches.length) return;
      touchStartYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (!e.touches || !e.touches.length) return;

      // если пользователь взаимодействует с внутри лежащим скроллящимся элементом — пусть он скроллится
      // найдём ближайший скроллящийся ancestor и проверим его кромки.
      const touch = e.touches[0];
      const startY = touchStartYRef.current;
      const deltaY = touch.clientY - startY;

      // ищем ближайший вертикально скроллящийся контейнер под пальцем
      const el = getClosestScrollable(e.target);

      // если нет отдельного скроллящегося элемента — работаем с target
      const container = el || target;

      const { scrollTop, scrollHeight, clientHeight } = container;

      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // тянем вниз (контент едет вверх) и уже в самом верху — блокируем bounce
      if (deltaY > 0 && atTop) {
        e.preventDefault();
        return;
      }
      // тянем вверх (контент едет вниз) и уже в самом низу — блокируем bounce
      if (deltaY < 0 && atBottom) {
        e.preventDefault();
        return;
      }
      // иначе — нормальный скролл
    };

    // важное: passive: false, иначе preventDefault проигнорируют
    const opts = { passive: false };
    target.addEventListener("touchstart", onTouchStart, opts);
    target.addEventListener("touchmove", onTouchMove, opts);

    // Дополнительно: iOS может слушать не на scrollingElement.
    // Чтобы надёжнее гасить резину в standalone, поставим дублирующий слушатель на window:
    window.addEventListener("touchmove", onTouchMove, opts);

    return () => {
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [enabled, rootRef]);

  const value = useMemo(() => ({ enabled, setEnabled }), [enabled]);

  return <OverscrollCtx.Provider value={value}>{children}</OverscrollCtx.Provider>;
}

// помогает найти ближайший вертикально-скроллящийся контейнер
function getClosestScrollable(node) {
  let el = node;
  while (el && el !== document.body && el !== document.documentElement) {
    if (isVerticallyScrollable(el)) return el;
    el = el.parentElement;
  }
  return null;
}

function isVerticallyScrollable(el) {
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  const canScroll =
    (overflowY === "auto" || overflowY === "scroll") &&
    el.scrollHeight > el.clientHeight;
  return canScroll;
}

// потребительскому коду — хук
export function useOverscroll() {
  return useContext(OverscrollCtx);
}
